import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private apiUrl = `${environment.apiUrl}/auth`;
    private tokenKey = 'auth_token';
    private refreshTokenKey = 'refresh_token';

    login(credentials: object): Observable<any> {
        return this.http.post(`${this.apiUrl}/login`, credentials).pipe(
            tap((res: any) => this.setSession(res))
        );
    }

    register(data: object): Observable<any> {
        return this.http.post(`${this.apiUrl}/register`, data);
    }

    logout(): void {
        const refreshToken = localStorage.getItem(this.refreshTokenKey);
        // Best effort to notify backend
        this.http.post(`${this.apiUrl}/logout`, { refreshToken }).subscribe({
            next: () => console.log('Logout successful on server'),
            error: (err) => console.error('Logout failed on server', err)
        });
        this.clearSession();
        this.router.navigate(['/login']);
    }

    refreshToken(): Observable<any> {
        const refreshToken = localStorage.getItem(this.refreshTokenKey);
        // Assuming backend expects { refreshToken: string } and returns new set
        return this.http.post(`${this.apiUrl}/refresh`, { refreshToken }).pipe(
            tap((res: any) => this.setSession(res))
        );
    }

    // Helper to save tokens
    private setSession(authResult: any): void {
        // Support both camelCase and PascalCase (backend returns PascalCase)
        const accessToken = authResult?.accessToken || authResult?.AccessToken;
        const refreshToken = authResult?.refreshToken || authResult?.RefreshToken;
        
        if (accessToken) {
            localStorage.setItem(this.tokenKey, accessToken);
        }
        if (refreshToken) {
            localStorage.setItem(this.refreshTokenKey, refreshToken);
        }
    }

    private clearSession(): void {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem(this.refreshTokenKey);
    }

    get token(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    get isAuthenticated(): boolean {
        return !!this.token; // Basic check, ideally check expiry
    }

    getUserRole(): string | null {
        try {
            const payload = this.decodeToken();
            if (!payload) return null;

            // Build-in role claim often has this key, or "role"
            return payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || payload['role'] || null;
        } catch (e) {
            console.error('Failed to decode token for role', e);
            return null;
        }
    }

    getUserInfo(): { userId: string, userName: string } | null {
        try {
            const payload = this.decodeToken();
            if (!payload) return null;

            const firstName = payload.firstName || '';
            const lastName = payload.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();

            return {
                userId: payload.userId || payload.sub || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
                userName: fullName || payload.unique_name || payload.name || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || 'Unknown User'
            };
        } catch (e) {
            console.error('Failed to decode token for user info', e);
            return null;
        }
    }

    private decodeToken(): any {
        const token = this.token;
        if (!token) return null;

        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            return JSON.parse(jsonPayload);
        } catch (e) {
            throw e;
        }
    }
}
